import { ConnectionType } from '@vectronic/desktop-api';
import { webDtos } from '@vectronic/openApi-admin';
import { dsDtos, dsEnums, userDtos, webEnums } from '@vectronic/openApi-customer';
import { formatDistanceToNowStrict, parseISO } from 'date-fns';
import { DateTime } from 'luxon';
import moment from 'moment';
import { guessTimezone } from './timezone/guess-timezone';
import { momentTimezone } from './timezone/moment-timezone';

export class Translator {
  static readonly fixType = (fixType: webEnums.FixType) => {
    switch (fixType) {
      case 'NO_FIX':
        return 'No Fix';
      case 'ONE_GPS_SAT':
        return '1 GPS Sat';
      case 'TWO_GPS_SAT':
        return '2 GPS Sats';
      case 'TWO_D_FIX':
        return '2D';
      case 'THREE_D_FIX_NOT_VALIDATED':
        return '3D';
      case 'THREE_D_FIX_VALIDATED':
        return '3D Valid';
      case undefined:
      case null:
        return '';
      default:
        return fixType;
    }
  };

  static readonly originCode = (originCode: webEnums.OriginCode) => {
    switch (originCode) {
      case 'GLOBALSTAR':
        return 'Globalstar';
      case 'IRIDIUM':
        return 'Iridium';
      case 'GSM_FTP':
        return 'GSM-FTP';
      case 'GSM_SMS':
        return 'GSM-SMS';
      case 'TERMINAL':
        return 'Terminal';
      case 'COLLAR':
        return 'Collar';
      case 'NB_IOT':
        return 'IoT';
      case 'IRIDIUM_EMAIL':
        return 'Iridium';
      case undefined:
      case null:
        return '';
      default:
        return originCode;
    }
  };

  static readonly originWithEmail = (originCode: webEnums.OriginCode) => {
    switch (originCode) {
      case 'GLOBALSTAR':
        return 'Globalstar';
      case 'IRIDIUM':
        return 'Iridium';
      case 'GSM_FTP':
        return 'GSM-FTP';
      case 'GSM_SMS':
        return 'GSM-SMS';
      case 'TERMINAL':
        return 'Terminal';
      case 'COLLAR':
        return 'Collar';
      case 'NB_IOT':
        return 'IoT';
      case 'IRIDIUM_EMAIL':
        return 'Iridium Email';
      case undefined:
      case null:
        return '';
      default:
        return originCode;
    }
  };

  static readonly deviceClassWithOwnIdPool = (deviceClass: webEnums.DeviceClassWithOwnIdPool) => {
    switch (deviceClass) {
      case 'CAMERA':
      case 'CONFIGURATION_DEVICE':
      case 'DATA_LOGGER':
      case 'DROP_OFF':
      case 'EXTERNAL_SENSOR':
      case 'COLLAR':
      case 'TRAP_TRANSMITTER':
      case 'TT4_SENSOR':
      case undefined:
      case null:
        return this.enumToLabel(deviceClass);
      default:
        return deviceClass;
    }
  };

  static readonly dataUploadSource = (uploadSource: webEnums.DataUploadSource) => {
    switch (uploadSource) {
      case 'GDX':
        return 'GDX';
      case 'HTTP_UPLOAD':
      case 'PROVIDER':
      case undefined:
      case null:
        return this.enumToLabel(uploadSource);
      default:
        return uploadSource;
    }
  };

  static readonly messageType = (messageType: webEnums.ExtendedMessageType) => {
    switch (messageType) {
      case 'NO_CONTACT':
      case 'MORTALITY':
      case 'STATUS':
      case 'NULL':
      case 'EXPELLED':
      case undefined:
      case null:
        return this.enumToLabel(messageType);
      default:
        return messageType;
    }
  };

  static readonly alive = (alive: boolean) => {
    if (alive === true) return 'Alive';
    if (alive === false) return 'Dead';
    return '';
  };

  static readonly expelled = (expelled?: boolean) => {
    if (expelled === true) return 'Expelled';
    if (expelled === false) return 'Implanted';
    return '';
  };

  static readonly sensorId = (sensorId: number) => {
    if (sensorId === 0) return '';
    return sensorId;
  };

  static readonly received = (received: boolean) => {
    if (received == null) return '';
    if (received) return 'Received';
    return 'Not received';
  };

  static readonly animalSex = (animalSex: webEnums.AnimalSex) => {
    switch (animalSex) {
      case 'FEMALE':
      case 'MALE':
      case 'UNKNOWN':
        return this.enumToLabel(animalSex);
      default:
        return 'Undefined';
    }
  };

  static readonly comType = (comType: webEnums.ComType) => {
    switch (comType) {
      case 'IRIDIUM':
        return 'Iridium';
      case 'GLOBALSTAR':
        return 'Globalstar';
      case 'GSM':
        return 'GSM';
      case 'NB_IOT':
        return 'IoT';
      case 'NO_COMMUNICATION':
        return 'No Communication';
      case undefined:
      case null:
        return '';
      default:
        return comType;
    }
  };

  static readonly comTypeShort = (comType: webEnums.ComType) => {
    switch (comType) {
      case 'IRIDIUM':
        return 'IR';
      case 'GLOBALSTAR':
        return 'GL';
      case 'GSM':
        return 'GSM';
      case 'NB_IOT':
        return 'IoT';
      case 'NO_COMMUNICATION':
        return 'SOB';
      case undefined:
      case null:
        return '';
      default:
        return comType;
    }
  };

  static readonly lightCondition = (lightCondition: webEnums.LightCondition) => {
    switch (lightCondition) {
      case 'BRIGHT':
      case 'TWILIGHT':
      case 'DARK':
      case 'UNDEFINED':
      case 'UNKNOWN':
      case undefined:
      case null:
        return this.enumToLabel(lightCondition);
      default:
        return lightCondition;
    }
  };

  static readonly mortalityStatus = (mortalityStatus: webEnums.MortalityStatus) => {
    switch (mortalityStatus) {
      case 'NOT_AVAILABLE':
        return 'N/A';
      case 'MORTALITY_NO_RADIUS':
        return 'Mortality';
      case 'NOTHING_DETECTED':
      case 'LOW_ACTIVITY_NO_RADIUS':
      case 'LOW_ACTIVITY_INSIDE_RADIUS':
      case 'LOW_ACTIVITY_OUTSIDE_RADIUS':
      case 'MORTALITY_INSIDE_RADIUS':
      case 'MORTALITY_OUTSIDE_RADIUS':
      case undefined:
      case null:
        return this.enumToLabel(mortalityStatus);
      default:
        return mortalityStatus;
    }
  };

  static readonly mortalityStatusShort = (mortalityStatus: webEnums.MortalityStatus) => {
    switch (mortalityStatus) {
      case 'NOTHING_DETECTED':
        return 'Alive';
      case 'NOT_AVAILABLE':
        return 'N/A';
      case undefined:
      case null:
        return '';
      default:
        return 'Dead';
    }
  };

  static readonly animalMortalityStatus = (
    animal: webDtos.AnimalDashboard | webDtos.AnimalExtended,
  ) => {
    if (!animal) return;
    if (animal?.dateOfDeath) {
      const deathDate = new Date(animal.dateOfDeath);
      const today = new Date();

      deathDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      if (deathDate <= today) {
        return 'Dead';
      }
    }

    const latestDeployment = this.getLatestDeployment(animal.deployments);
    if (latestDeployment?.endType === 'DEAD') return 'Dead';
    const lastPosMortStatus =
      'lastPosMortStatus' in animal
        ? animal.lastPosMortStatus
        : (animal as webDtos.AnimalExtended).lastPosition?.mortalityStatus;
    return Translator.mortalityStatusShort(lastPosMortStatus);
  };

  static readonly getLatestDeployment = (deployments: webDtos.Deployment[]) => {
    if (!deployments || deployments.length === 0) {
      return null;
    }

    return deployments.reduce((latest, current) => {
      const currentDate = new Date(current.startDate);
      const latestDate = new Date(latest.startDate);
      return currentDate > latestDate ? current : latest;
    });
  };

  static getDeploymentMortalityStatus(latestDeployment: webDtos.Deployment): string | undefined {
    if (!latestDeployment) {
      return 'Unknown';
    }

    if (latestDeployment.endType === 'DEAD' || latestDeployment.endDate) {
      return 'Dead';
    }

    const currentDate = new Date();
    const startDate = new Date(latestDeployment.startDate);
    const endDate = latestDeployment.endDate ? new Date(latestDeployment.endDate) : null;
    const isDeployed = startDate < currentDate && (!endDate || endDate > currentDate);

    if (isDeployed) {
      return undefined;
    }

    return 'Unknown';
  }

  static isDeploymentActive(deployment: webDtos.Deployment) {
    const currentDate = new Date();
    const startDate = new Date(deployment.startDate);
    const endDate = deployment.endDate ? new Date(deployment.endDate) : null;
    return startDate < currentDate && (!endDate || endDate > currentDate);
  }

  static readonly mortalityKind = (status: webEnums.Status) => {
    switch (status) {
      case 'MORTALITY':
      case 'LOW_ACTIVITY':
      case 'LATERAL':
      case undefined:
      case null:
        return this.enumToLabel(status);
      default:
        return status;
    }
  };

  static readonly networkStatus = (
    networkStatus: webDtos.TimespanNetworkStatus.DataEnum | '',
    airtimeNetworkStatusChangeRequested?: webEnums.AirtimeNetworkStatusChangeRequested,
  ) => {
    if (airtimeNetworkStatusChangeRequested === 'ACTIVATED') return 'Activation Requested';
    else if (airtimeNetworkStatusChangeRequested === 'DEACTIVATED') return 'Deactivation Requested';
    switch (networkStatus) {
      case 'ACTIVATING':
      case 'ACTIVATED':
      case 'DEACTIVATING':
      case 'DEACTIVATED':
      case 'SUSPENDING':
      case 'RESUMING':
      case 'CHANGING':
      case 'INVALID':
        return this.enumToLabel(networkStatus);
      default:
        return networkStatus;
    }
  };

  static readonly trimLatLonDecimals = (latlon: number) => {
    if (latlon) return latlon.toFixed(5);
  };

  static readonly trimTemperatureDecimals = (temperature: number) => {
    if (temperature) return temperature.toFixed(2);
  };

  static readonly positionChannel = (positionChannel: string | number) => positionChannel || '';

  static readonly trimString = (stringToSlice: string, length = 18) => {
    if (!stringToSlice) return '';
    if (stringToSlice.length <= length) return stringToSlice;
    return stringToSlice.slice(0, length) + '...';
  };

  static readonly trimUTMDecimals = (utmCoords: number) => {
    if (utmCoords) return utmCoords.toFixed(0);
    return utmCoords;
  };

  static readonly convertSecondsToDays = (seconds: number) => {
    if (!seconds) return '';
    seconds = Number(seconds);
    const d = Math.floor(seconds / (3600 * 24));
    if (d > 0) {
      return d + (d == 1 ? ' day ' : ' days ');
    }
    return '';
  };

  static readonly getRoundNumber = (number: number) => {
    if (!number) return '';
    return Math.round(number);
  };

  static readonly addLargeSpaceInBetweenToTimestampSting = (timeStamp: string) =>
    typeof timeStamp !== 'undefined'
      ? timeStamp.replace('T', '\u00A0\u00A0').replace('Z', '')
      : timeStamp;

  static readonly commandStateColor = (commandState: dsEnums.CommandState) => {
    switch (commandState) {
      case 'ACKNOWLEDGED':
        return { 'background-color': DataTableColor.INDIAN_GREEN };
      case 'CANCELED':
        return { 'background-color': DataTableColor.INDIAN_GRAY };
      case 'CREATED':
      case 'SENT_TO_DEVICE':
      case 'SENT_TO_SERVER':
        return { 'background-color': DataTableColor.INDIAN_BLUE };
      case 'FAILED_ON_DEVICE':
      case 'FAILED_SENDING_TO_DEVICE':
      case 'FAILED_SENDING_TO_SERVER':
      case 'FAILED_EXPIRED':
      case 'FAILED_UNSPECIFIED':
        return { 'background-color': DataTableColor.INDIAN_RED };
    }
  };

  static readonly commandState = (commandState: dsEnums.CommandState) => {
    switch (commandState) {
      case 'ACKNOWLEDGED':
      case 'CANCELED':
      case 'CREATED':
      case 'SENT_TO_DEVICE':
      case 'SENT_TO_SERVER':
      case 'FAILED_ON_DEVICE':
      case 'FAILED_SENDING_TO_DEVICE':
      case 'FAILED_SENDING_TO_SERVER':
      case 'FAILED_EXPIRED':
      case 'FAILED_UNSPECIFIED':
        return this.enumToLabel(commandState);
      default:
        return commandState;
    }
  };

  static readonly deviceCommand = (deviceCommand?: {
    deviceCommandType?: string;
    data?: string;
  }) => {
    if (!deviceCommand) return;
    const { deviceCommandType, data } = deviceCommand;
    if (deviceCommandType === 'DROP_OFF_RELEASE' || deviceCommandType === 'DROP_OFF_RELEASE_LONG') {
      return `Drop Off Release: ${data}`;
    }
    return `${deviceCommandType}: ${data}`;
  };

  static readonly connectionType = (type: ConnectionType, isBluetoothLost?: boolean) => {
    if (![ConnectionType.BLUETOOTH, ConnectionType.NATIVE_BLUETOOTH].includes(type)) return type;
    return isBluetoothLost ? `Bluetooth (lost)` : `Bluetooth`;
  };

  static readonly version = (obj: dsDtos.BootloaderVersion) =>
    obj ? `${obj.version.major}.${obj.version.minor}.${obj.version.release}` : '';

  static readonly comProvider = (comProvider: webDtos.TimespanComProvider.DataEnum | '') => {
    switch (comProvider) {
      case 'GROUND_CONTROL':
        return 'Ground Control';
      case 'MET_OCEAN':
        return 'metOcean';
      case 'GLOBALSTAR':
        return 'Globalstar';
      case 'ORBCOMM':
        return 'Orbcomm';
      case 'IRIDIUM_JAPAN':
        return 'Iridium Japan';
      default:
        return comProvider;
    }
  };

  static readonly dataPlan = (dataPlan: webDtos.TimespanDataPlan.DataEnum | '') => {
    switch (dataPlan) {
      case 'UNLIMITED':
        return 'Unlimited';
      case 'THREE_KBYTE':
        return '3 kB';
      case 'SMITHSONIAN_SPECIAL':
        return 'Smithsonian';
      case 'POOLED_THREE_KBYTE':
        return 'Economy';
      case 'POOLED_TWELVE_KBYTE':
        return 'Premium';
      case 'TEST_2_5_KBYTE':
        return '2.5 kB Test';
      case 'TEST_4_KBYTE':
        return '4 kB Test';
      default:
        return dataPlan;
    }
  };

  static readonly antena = (antena: webDtos.Build.AntennaEnum) => {
    switch (antena) {
      case 'VHF':
        return 'VHF';
      case 'UHF':
        return 'UHF';
      case 'VHF_AND_UHF':
        return 'VHF + UHF';
      default:
        return '';
    }
  };

  static readonly role = (role: webEnums.Role) => {
    switch (role) {
      case 'OWNER':
      case 'DATA_MANAGER':
      case 'DEVICE_MANAGER':
      case 'READER':
        return this.enumToLabel(role);
      default:
        return role;
    }
  };

  static formatDateDistance(dateString) {
    if (!dateString) return;
    const date = parseISO(dateString);
    return formatDistanceToNowStrict(date) + ' ago';
  }

  static utcDateWithoutTime(dateString: string) {
    if (!dateString) return '';
    if (dateString.slice(-1) !== 'Z') dateString += 'Z';
    const date = new Date(dateString);
    const pad = (n: number) => (n < 10 ? '0' : '') + n;
    const day = pad(date.getUTCDate());
    const month = pad(date.getUTCMonth() + 1);
    const year = date.getUTCFullYear();
    return `${year}-${month}-${day}`;
  }

  static utcDate(dateString: string) {
    if (!dateString) return '';
    if (dateString.slice(-1) !== 'Z') dateString += 'Z';
    const date = new Date(dateString);
    const pad = (n: number) => (n < 10 ? '0' : '') + n;
    const day = pad(date.getUTCDate());
    const month = pad(date.getUTCMonth() + 1);
    const year = date.getUTCFullYear();
    const hours = pad(date.getUTCHours());
    const minutes = pad(date.getUTCMinutes());
    const seconds = pad(date.getUTCSeconds());
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  static utcDateWithMillies(dateString: string) {
    if (!dateString) return '';
    if (dateString.slice(-1) !== 'Z') dateString += 'Z';
    const date = new Date(dateString);
    const pad = (n: number, millies?: boolean) =>
      (n < 100 && millies ? '0' : '') + (n < 10 ? '0' : '') + n;
    const day = pad(date.getUTCDate());
    const month = pad(date.getUTCMonth() + 1);
    const year = date.getUTCFullYear();
    const hours = pad(date.getUTCHours());
    const minutes = pad(date.getUTCMinutes());
    const seconds = pad(date.getUTCSeconds());
    const millies = pad(date.getUTCMilliseconds(), true);
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}:${millies}`;
  }

  static lmtDate(dateString: string, { timezone, timezoneType }: userDtos.UserPreferences) {
    if (!dateString) return '';
    if (dateString.slice(-1) !== 'Z') dateString += 'Z';
    if (timezoneType === 'custom') timezone ??= guessTimezone();
    else timezone = guessTimezone();
    return momentTimezone(new Date(dateString), timezone)
      .milliseconds(0)
      .toISOString(true)
      ?.replace('.000', '')
      ?.replace('T', ' ');
  }

  static dateSortValue(dateString: string) {
    if (!dateString) return 0;
    return new Date(dateString).getTime();
  }

  static headerName(camelCase: string) {
    return this.camelCase(camelCase);
  }
  static camelCase(camelCase: string) {
    if (!camelCase) return '';
    const withSpaces = camelCase.replace(/([a-z])([A-Z])/g, '$1 $2');
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  }

  static beaconFrequency(value: string | number) {
    if (!value) return '';
    const number = typeof value === 'number' ? value : parseInt(value);
    return (number / 1000000).toFixed(3);
  }

  static activityOrientation(
    activity: webDtos.Activity,
    collar: webDtos.CollarExtended,
    type: 'x' | 'y' | 'z',
  ) {
    const { activity1, activity2, activity3 } = activity;
    const channel = collar?.deviceTypeActivityChannelOrientations?.[type];
    if (channel === 'CHANNEL_1') return activity1;
    if (channel === 'CHANNEL_2') return activity2;
    if (channel === 'CHANNEL_3') return activity3;
    return '';
  }

  static byteSize(bytes: number) {
    if (!bytes) return 'n.a.';
    const units = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`;
  }

  static readonly parseComType = (comTypeString: string) => {
    switch (comTypeString) {
      case 'Iridium':
        return 'IRIDIUM';
      case 'Globalstar':
        return 'GLOBALSTAR';
      case 'GSM-6bit':
        return 'GSM6bit';
      case 'GSM-7bit':
        return 'GSM7bit';
      case 'GSM-8bit':
        return 'GSM8bit';
      case 'NB-IoT':
        return 'NB_IOT';
      case undefined:
      case null:
        return '';
      default:
        return comTypeString;
    }
  };

  static milliVolt(value?: number) {
    if (value == null) return '';
    return (value / 1000).toFixed(2);
  }

  static fourByteHex(value: number) {
    const hex = (value >>> 0).toString(16).padStart(8, '0').toUpperCase();
    return [hex.slice(0, 2), hex.slice(2, 4), hex.slice(4, 6), hex.slice(6, 8)].join('.');
  }

  static readonly logType = (logType: webEnums.LogType) => {
    switch (logType) {
      case 'SYSTEM_LOG':
      case 'TRANSMISSION_LOG':
      case undefined:
      case null:
        return this.enumToLabel(logType);
      default:
        return logType;
    }
  };

  static mitHeartRateToMortalityStatus(mitHeartRate: number): 'MORTALITY' | 'ALIVE' {
    if (mitHeartRate == undefined) {
      return undefined;
    }
    if (mitHeartRate == 0) {
      return 'MORTALITY';
    }
    return 'ALIVE';
  }

  static readonly releaseTimeMode = (releaseTimeMode: webEnums.ReleaseTimeMode) => {
    switch (releaseTimeMode) {
      case 'ABSOLUTE':
      case 'RELATIVE':
      case undefined:
      case null:
        return this.enumToLabel(releaseTimeMode);
      default:
        return releaseTimeMode;
    }
  };

  static releaseTimeToString(releaseTime: number, releaseTimeMode: webEnums.ReleaseTimeMode) {
    if (releaseTimeMode === 'ABSOLUTE')
      return moment.unix(releaseTime + 946684800).format('YYYY-MM-DD HH:mm');

    if (releaseTimeMode === 'RELATIVE')
      return convertMillisecondToFormattedTimeSpan(releaseTime, 0);

    return releaseTime;

    /**
     * Converts milliseconds to a formatted time string.
     * @param {number} milliseconds - The number of milliseconds to convert.
     * @param {number} factor - The factor in milliseconds for rounding.
     * @returns {string} The formatted time string.
     */
    function convertMillisecondToFormattedTimeSpan(milliseconds: number, factor: number): string {
      const msPerSecond = 1000;
      const msPerMinute = 60 * msPerSecond;
      const msPerHour = 60 * msPerMinute;
      const msPerDay = 24 * msPerHour;
      const msPerMonth = 30 * msPerDay;
      const msPerYear = 365 * msPerDay;

      const units = [
        { label: 'y', value: Math.floor(milliseconds / msPerYear), threshold: msPerYear },
        { label: 'mo', value: 0, threshold: msPerMonth },
        { label: 'd', value: 0, threshold: msPerDay },
        { label: 'h', value: 0, threshold: msPerHour },
        { label: 'm', value: 0, threshold: msPerMinute },
        { label: 's', value: 0, threshold: msPerSecond },
        { label: 'ms', value: 0, threshold: 1 },
      ];

      milliseconds %= msPerYear;
      units[1].value = Math.floor(milliseconds / msPerMonth);
      milliseconds %= msPerMonth;
      units[2].value = Math.floor(milliseconds / msPerDay);
      milliseconds %= msPerDay;
      units[3].value = Math.floor(milliseconds / msPerHour);
      milliseconds %= msPerHour;
      units[4].value = Math.floor(milliseconds / msPerMinute);
      milliseconds %= msPerMinute;
      units[5].value = Math.floor(milliseconds / msPerSecond);
      milliseconds %= msPerSecond;
      units[6].value = milliseconds;

      const filteredUnits = units.filter((u) => u.value > 0 && factor <= u.threshold).slice(0, 3); // Keep only the top 3 units
      return filteredUnits.map((u) => `${u.value} ${u.label}`).join(' ');
    }
  }

  static deploymentDateRange({ startDate, endDate }: userDtos.Deployment) {
    const startString = DateTime.fromISO(startDate).toFormat('dd MMM yyyy');
    const endString = endDate ? DateTime.fromISO(endDate).toFormat('dd MMM yyyy') : 'Present';
    return `${startString} - ${endString}`;
  }

  static deploymentAge(age: webEnums.Age) {
    switch (age) {
      case 'SIX_MONTHS':
        return '6 months';
      case 'YEARLING':
        return '1 year';
      case 'TWO_TO_NINE_YEARS':
        return '2–9 years';
      case 'TEN_TO_FIFTEEN_YEARS':
        return '10–15 years';
      case 'OVER_FIFTEEN_YEARS':
        return '>15 years';
      default:
        return '';
    }
  }

  static deploymentEndType(endType: webEnums.EndType) {
    switch (endType) {
      case 'FALL_OFF':
        return 'Fall Off';
      case 'DEAD':
        return 'Dead';
      case 'REMOVED':
        return 'Removed';
      case 'DROP_OFF_ACTIVATED':
        return 'Drop Off Activated';
      case 'REDEPLOYED':
        return 'Redeployed';
      default:
        return '';
    }
  }

  static roundPosition(position: webDtos.GPSPosition) {
    return `${position?.latitude?.toFixed(5)}/${position?.longitude?.toFixed(5)}`;
  }

  static species(species: dsDtos.AnimalSpecies) {
    return species?.english || species?.latin || '';
  }

  static manufacturingStatusToColor(status: webEnums.ManufacturingStatus) {
    switch (status) {
      case 'New':
        return 'red';
      case 'Assigned':
        return 'crimson';
      case 'ReadyForTest':
        return 'royalblue';
      case 'Testing':
        return 'deepskyblue';
      case 'ManufacturingCompleted':
        return 'steelblue';
      case 'ManufacturingFailed':
        return 'orangered';
      case 'Packed':
        return 'mediumseagreen';
      case 'Shipped':
        return 'green';
      case 'Canceled':
        return 'gray';
    }
  }

  static manufacturingStatus(status: webEnums.ManufacturingStatus) {
    if (status === 'ManufacturingCompleted') {
      return 'Manufactured';
    }
    return status;
  }

  static enumToOptions<T extends string>(
    enumObject: Record<string, T>,
    translator: (value: T) => string = (value) => this.enumToLabel(value),
  ): { label: string; item: T }[] {
    return Object.values(enumObject).map((value) => ({
      label: translator(value),
      item: value,
    }));
  }

  private static enumToLabel(value: string) {
    if (!value) return '';
    return value
      .split('_')
      .map((word) => {
        const lower = word.toLowerCase();
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(' ');
  }
}

export enum DataTableColor {
  INDIAN_GREEN = '#5CCD5C',
  INDIAN_YELLOW = '#CDCD5C',
  INDIAN_RED = '#CD5C5C',
  INDIAN_BLUE = '#0E88D3',
  INDIAN_GRAY = '#C6C6C6',
}
